import useInfinityList from '@components/Hooks/infinity-list/useInfinityList';
import * as R from 'ramda';
import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import productDetailsRepository from '../../../../../services/modules/productDetails/productDetails.repository';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import useProductDetailsStore from '../../../../../stores/ProductDetails/productDetails.store';
import { ProductDetailsOriginType } from '../../../../../stores/ProductDetails/productDetails.types';
import useIsAdminSeller from '../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { ESProduct } from '../../../product.types.v2';
import { productSearchRepository } from '../../../repositories/productSearch/productSearch.repository';
import { UseSuggestedProductsParams } from '../suggestedProducts.types';
import useSuggestedProductsColumns from './useSuggestedProductsColumns';

const ITEMS_PER_PAGE = 12;

const useSuggestedProducts = ({
  productId,
  productColorId,
  isOfflineMode = false,
}: UseSuggestedProductsParams) => {
  const [title, setTitle] = useState<string>();
  const [isLoadingTitle, setIsLoadingTitle] = useState(true);
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
  const [loadMoreElement, setLoadMoreElement] = useState<HTMLDivElement | null>(null);

  const isAdminSeller = useIsAdminSeller();

  const { setHasSuggestedProducts, from } = useProductDetailsStore(
    useShallow((state) => ({
      setHasSuggestedProducts: state.setHasSuggestedProducts,
      from: state.from,
    })),
  );
  const catalogId = from?.type === ProductDetailsOriginType.CATALOG ? from.id : undefined;
  const [selectedOrder] = useOrderBookStore(useShallow((store) => [store.selectedOrder]));

  const listFetcher = productDetailsRepository(isOfflineMode).getSuggestedProducts(productColorId);

  const { state, loadMoreRows } = useInfinityList<ESProduct>(listFetcher, {
    isAdminSeller,
    itemsPerPage: ITEMS_PER_PAGE,
    orderId: selectedOrder?.id,
    catalogId,
    sortBy: 'code',
  });

  const columns = useSuggestedProductsColumns(gridElement);

  const fetchTitle = useCallback(async () => {
    const repository = productSearchRepository();

    const response = await repository.getProductSearchSuggestedClassifications({
      productId,
      productColorId,
    });

    if (response.ok) {
      setTitle(response.data?.map((classification) => classification.name).join(' & '));
    }

    setIsLoadingTitle(false);
  }, [productColorId, productId]);

  useEffect(() => {
    fetchTitle();
  }, [fetchTitle]);

  const products = state.items.filter((product) => product.productColorId !== productColorId);
  const isHidden = !isLoadingTitle && (R.isEmpty(state.totalItems) || !title);
  const hasLoadedFirstPage = !state.isLoadingPage && state.page > 0;

  useEffect(() => {
    if (isHidden) {
      setHasSuggestedProducts(false);
      return;
    }

    if (isLoadingTitle || !hasLoadedFirstPage) {
      return;
    }

    setHasSuggestedProducts(products.length > 0);
  }, [isHidden, isLoadingTitle, hasLoadedFirstPage, products.length, setHasSuggestedProducts]);

  useEffect(() => {
    if (!loadMoreElement || !state.hasMore) {
      return;
    }

    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadMoreRows();
      }
    });

    intersectionObserver.observe(loadMoreElement);

    return () => intersectionObserver.disconnect();
  }, [loadMoreElement, state.hasMore, loadMoreRows]);

  return {
    columns,
    products,
    hasMoreProducts: state.hasMore,
    isHidden,
    isEmpty: hasLoadedFirstPage && products.length === 0,
    setGridElement,
    setLoadMoreElement,
  };
};

export default useSuggestedProducts;
